import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  Button,
  DialogActions,
  Tabs,
  Tab,
  Typography,
} from "@mui/material"

import { Formik } from "formik"
import { transformErrorForForm } from "../utils/utils"
import * as yup from "yup"
import { useTranslation } from "react-i18next"
import { useSupportiImport, SupportiImportPayload, SupportiImportResponse } from "../hooks/supporti"
import { MuiFileInput } from "mui-file-input"
import { useState, useCallback } from "react"
import { useSnackbarMessage } from "../hooks/snackbar"

type SupportiImportProps = {
  open: boolean
  onClose: () => void
}

const schema = yup.object().shape({
  file: yup.mixed().required("File is required"),
})

function hasErrors(response: SupportiImportResponse): boolean {
  return response.errors && Object.keys(response.errors).length > 0
}

function ErrorItem({ row, errors }: { row: string; errors: Record<string, string> }) {
  const errorsAsList = Object.entries(errors).map(([key, error]) => `${key}: ${error}`)
  const { t } = useTranslation()

  const errorsAsString = errorsAsList.join(", ")

  return (
    <div key={row}>
      {t("general.riga")} {row}: {errorsAsString}
    </div>
  )
}

export default function SupportiImport({ open, onClose }: SupportiImportProps) {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useSupportiImport()
  const initialValues = {
    file: "",
    commit: false,
  } as unknown as SupportiImportPayload

  const [currentTab, setCurrentTab] = useState<"file" | "commit">("file")
  const [currentResponse, setCurrentResponse] = useState<any>(null)
  const [currentErrors, setCurrentErrors] = useState<any>(null)
  const [currentFile, setCurrentFile] = useState<File | null>(null)

  const handleClose = useCallback(
    (reset: () => void) => {
      setCurrentTab("file")
      setCurrentResponse(null)
      setCurrentErrors(null)
      setCurrentFile(null)
      onClose()
      reset && reset()
    },
    [onClose]
  )

  const showMessage = useSnackbarMessage()

  return (
    <Formik
      validationSchema={schema}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values, formikBag) => {
        const submitValues = {
          file: values.file,
          commit: currentTab === "commit",
        } as SupportiImportPayload
        console.log("Submitting import with values:", submitValues)
        try {
          const response = await mutateAsync(submitValues)

          setCurrentResponse(response)
          setCurrentFile(values.file)
          
          if (hasErrors(response)) {
            formikBag.setFieldValue("file", null)
            setCurrentErrors(response.errors)
          } else {
            setCurrentErrors(null)
            if (currentTab === "file") {
              setCurrentTab("commit")
            } else {
              handleClose(formikBag.resetForm)
              setCurrentFile(null)
              showMessage("Supporti importati correttamente", "success")
            }
          }
        } catch (e) {
          console.error("Error in import submission", e)
          const localError = transformErrorForForm(e)
          if (currentTab === "file") {
            formikBag.setErrors(localError)
          } else {
            showMessage("Errore durante l'importazione dei supporti", "error")
          }
        }
      }}
    >
      {({ handleSubmit, setFieldValue, values, resetForm }) => {
        return (
          <Dialog
            open={open}
            onClose={() => {
              handleClose(resetForm)
            }}
            fullWidth
            maxWidth="md"
          >
            <form onSubmit={handleSubmit} noValidate>
              <DialogTitle>Import Supporti</DialogTitle>
              <DialogContent>
                <Tabs value={currentTab}>
                  <Tab value="file" label="Seleziona file" />
                  <Tab value="commit" label="Importa" />
                </Tabs>

                {currentTab === "file" && (
                  <Grid container spacing={1} sx={{ py: 2 }}>
                    <Grid size={12}>
                      <MuiFileInput
                        name="file"
                        label={t("ente.nome")}
                        required
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        value={values.file}
                        onChange={(file) => {
                          if (file) {
                            setFieldValue("file", file)
                          }
                        }}
                      />
                    </Grid>
                    {currentFile && (
                      <Grid size={12}>
                        <Typography variant="body2" color="textSecondary">
                          {t("supporto.file_selezionato")}: {currentFile.name}
                        </Typography>
                      </Grid>
                    )}
                    {currentErrors && (
                      <>
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                          {t("supporto.errori_importazione")}
                        </Typography>
                        <Grid size={12}>
                          {Object.entries(currentErrors).map(([key, error]) => (
                            <ErrorItem key={key} row={key} errors={error as Record<string, string>} />
                          ))}
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}

                {currentTab === "commit" && (
                  <Grid container spacing={1} sx={{ py: 2 }}>
                    {currentResponse.supporti_update && currentResponse.supporti_update.length > 0 && (
                      <Grid size={12}>
                        <div>
                          <b>{t("supporto.supporti_aggiornati")}</b>
                        </div>
                        {currentResponse.supporti_update.map((supporto: any, index: number) => (
                          <span key={index}>{supporto}&nbsp;</span>
                        ))}
                      </Grid>
                    )}
                    {currentResponse.supporti_new && currentResponse.supporti_new.length > 0 && (
                      <Grid size={12}>
                        <div>
                          <b>{t("supporto.supporti_inseriti")}</b>
                        </div>
                        {currentResponse.supporti_new.map((supporto: any, index: number) => (
                          <span key={index}>{supporto}&nbsp;</span>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                )}
              </DialogContent>

              <Button type="submit" color="primary" sx={{ display: "none" }} disabled={isPending}></Button>
              <DialogActions sx={{ justifyContent: "space-between" }}>
                <Button onClick={() => handleClose(resetForm)} color="primary" variant="text">
                  {t("general.annulla")}
                </Button>
                <Button type="submit" color="primary" variant="contained" disabled={isPending}>
                  {t("general.importa")}
                </Button>
              </DialogActions>
            </form>
          </Dialog>
        )
      }}
    </Formik>
  )
}
